import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toastError, toastSuccess, toastLoading, toastDismiss } from '../utils/toastHelper'
import { ApiClient } from '../services/api'

export interface TipoEspacio {
  id: string
  nombre: string
  descripcion?: string
  tarifa_base_tipo: number
  tarifa_base_tipo_multiple?: number
  capacidad_maxima: number
  comodidades?: Record<string, any> | any[] | null
  unidad_reserva: string // 'noche' o 'dia'
  reservable: boolean
  privado?: boolean  // Si es privado (solo admin) o público (sitio web)
  empresa_id: number
  fecha_creacion: string
  fecha_actualizacion: string
}

export interface TipoEspacioCreateRequest {
  nombre: string
  descripcion?: string
  tarifa_base_tipo: number
  tarifa_base_tipo_multiple?: number
  capacidad_maxima: number
  comodidades?: Record<string, any>
  unidad_reserva?: string // 'noche' o 'dia'
  reservable?: boolean
  privado?: boolean  // Si es privado (solo admin) o público (sitio web)
}

export interface TipoEspacioUpdateRequest {
  nombre?: string
  descripcion?: string
  tarifa_base_tipo?: number
  tarifa_base_tipo_multiple?: number
  capacidad_maxima?: number
  comodidades?: Record<string, any>
  unidad_reserva?: string
  reservable?: boolean
  privado?: boolean  // Si es privado (solo admin) o público (sitio web)
}

/**
 * Hook para listar tipos de espacios
 */
export const useTiposEspacios = (limite: number = 50, offset: number = 0) => {
  return useQuery({
    queryKey: ['tipos-espacios', limite, offset],
    queryFn: async () => {
      return await ApiClient.get<TipoEspacio[]>(
        `/api/admin-protected/tipos-espacios?limite=${limite}&offset=${offset}`
      )
    },
  })
}

/**
 * Hook para obtener un tipo de espacio por ID
 */
export const useObtenerTipoEspacio = (tipoEspacioId: string | null) => {
  return useQuery({
    queryKey: ['tipo-espacio', tipoEspacioId],
    queryFn: async () => {
      if (!tipoEspacioId) return null
      return await ApiClient.get<TipoEspacio>(
        `/api/admin-protected/tipos-espacios/${tipoEspacioId}`
      )
    },
    enabled: !!tipoEspacioId,
  })
}

/**
 * Hook para crear un tipo de espacio
 */
export const useCrearTipoEspacio = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: TipoEspacioCreateRequest) => {
      return await ApiClient.post<TipoEspacio>(
        '/api/admin-protected/tipos-espacios',
        request
      )
    },
    onSuccess: (data) => {
      toastSuccess(`Tipo de espacio "${data.nombre}" creado exitosamente`)
      queryClient.invalidateQueries({ queryKey: ['tipos-espacios'] })
    },
    onError: (error: any) => {
      toastError(error.response?.data?.detail || 'Error al crear el tipo de espacio')
    },
  })
}

/**
 * Hook para actualizar un tipo de espacio
 */
export const useActualizarTipoEspacio = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { tipoEspacioId: string; data: TipoEspacioUpdateRequest }) => {
      return await ApiClient.put<TipoEspacio>(
        `/api/admin-protected/tipos-espacios/${params.tipoEspacioId}`,
        params.data
      )
    },
    onSuccess: (data) => {
      toastSuccess(`Tipo de espacio "${data.nombre}" actualizado exitosamente`)
      queryClient.invalidateQueries({ queryKey: ['tipos-espacios'] })
    },
    onError: (error: any) => {
      toastError(error.response?.data?.detail || 'Error al actualizar el tipo de espacio')
    },
  })
}

/**
 * Hook para eliminar un tipo de espacio
 */
export const useEliminarTipoEspacio = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (tipoEspacioId: string) => {
      return await ApiClient.delete(`/api/admin-protected/tipos-espacios/${tipoEspacioId}`)
    },
    onSuccess: () => {
      toastSuccess('Tipo de espacio eliminado exitosamente')
      queryClient.invalidateQueries({ queryKey: ['tipos-espacios'] })
    },
    onError: (error: any) => {
      toastError(error.response?.data?.detail || 'Error al eliminar el tipo de espacio')
    },
  })
}
